import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ArtistLayout from "@/components/layout/ArtistLayout";
import { useAuth, API } from "@/App";
import axios from "axios";
import { ArrowLeft, User, MapPin, Calendar, Hash, PlayCircle, Edit, XCircle, AlertTriangle, Clock, DollarSign } from "lucide-react";
import { Button } from "@/components/ui/button";

const ArtistSubmissionDetail = () => {
  const { id } = useParams();
  const { token } = useAuth();
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSubmission = async () => {
      try {
        const response = await axios.get(`${API}/artist/submissions/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSubmission(response.data);
      } catch (error) {
        console.error("Failed to fetch submission:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchSubmission();
  }, [id, token]);

  const MediaPreview = ({ url, type, label }) => {
    const urls = Array.isArray(url) ? url : url ? [url] : [];
    
    if (urls.length === 0) {
      return (
        <div className="border border-gray-800 p-4 bg-gray-900">
          <p className="text-gray-500 text-sm uppercase tracking-wider mb-2">{label}</p>
          <div className="bg-black aspect-video flex items-center justify-center">
            <p className="text-gray-600">Not uploaded</p>
          </div>
        </div>
      );
    }

    return (
      <div className="border border-gray-800 p-4 bg-gray-900 hover:border-[#00B4E5] transition-colors">
        <p className="text-gray-500 text-sm uppercase tracking-wider mb-2">{label} ({urls.length})</p>
        <div className={urls.length === 1 ? "" : "grid grid-cols-2 gap-2"}>
          {urls.map((singleUrl, idx) => {
            const isVideo = type === "video" || singleUrl.includes("/video/") || singleUrl.includes(".mp4");
            const isAudio = type === "audio" || singleUrl.includes(".mp3") || singleUrl.includes(".wav");
            return (
              <div key={idx} className={`bg-black ${urls.length === 1 ? "aspect-video" : "aspect-square"} flex items-center justify-center overflow-hidden rounded`}>
                {isVideo ? (
                  <video src={singleUrl} controls className="w-full h-full object-cover" />
                ) : isAudio ? (
                  <div className="p-4 w-full"><audio src={singleUrl} controls className="w-full" /></div>
                ) : (
                  <img src={singleUrl} alt={label} className="w-full h-full object-cover" />
                )}
              </div>
            );
          })}
        </div>
        <div className="mt-2 flex flex-wrap gap-2">
          {urls.map((singleUrl, idx) => (
            <a key={idx} href={singleUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-[#00B4E5] hover:underline">
              {urls.length === 1 ? "Open in new tab" : `File ${idx + 1}`}
            </a>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <ArtistLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-400">Loading submission...</p>
        </div>
      </ArtistLayout>
    );
  }

  if (!submission) {
    return (
      <ArtistLayout>
        <div className="text-center py-12">
          <p className="text-gray-400 mb-4">Submission not found</p>
          <Link to="/artist" className="text-[#00B4E5] hover:underline">
            Back to dashboard
          </Link>
        </div>
      </ArtistLayout>
    );
  }

  return (
    <ArtistLayout>
      <div className="animate-fade-in" data-testid="artist-submission-detail">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/artist" 
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-4"
          >
            <ArrowLeft size={20} />
            Back to dashboard
          </Link>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-4xl font-heading text-white mb-2">{submission.venue}</h1>
              <div className="flex items-center gap-3">
                <span className={`status-badge ${submission.status}`}>
                  {submission.status.replace(/_/g, ' ')}
                </span>
                <span className="text-gray-500">{submission.city} • {submission.date}</span>
              </div>
            </div>
            {(submission.status === "draft" || (submission.status === "rejected" && submission.resubmission_required)) && (
              <Link to={`/artist/submission/${id}/edit`}>
                <Button variant="outline" className="border-gray-700 text-white hover:bg-gray-800">
                  <Edit size={18} className="mr-2" />
                  {submission.status === "rejected" ? "Resubmit" : "Edit Submission"}
                </Button>
              </Link>
            )}
          </div>
        </div>

        {/* Rejection Info (if rejected) */}
        {submission.status === "rejected" && submission.rejection_reason && (
          <div className="bg-red-900/30 border border-red-700 p-6 mb-6 rounded-lg">
            <h3 className="font-semibold text-red-400 mb-2 flex items-center gap-2">
              <XCircle size={18} />
              Submission Rejected
            </h3>
            <p className="text-red-300 mb-3">{submission.rejection_reason}</p>
            <div className="flex flex-wrap gap-4 text-sm text-red-400">
              {submission.resubmission_required && (
                <span className="inline-flex items-center gap-1 bg-red-900/50 px-2 py-1 rounded">
                  <AlertTriangle size={14} />
                  Resubmission Required - Please edit and resubmit
                </span>
              )}
              {submission.rejected_at && (
                <span>Rejected on: {new Date(submission.rejected_at).toLocaleString()}</span>
              )}
            </div>
          </div>
        )}

        {/* Deadline Info */}
        {submission.submission_deadline && (
          <div className="bg-gray-900 border border-gray-800 p-4 mb-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Clock size={20} className="text-[#00B4E5]" />
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Submission Deadline</p>
                <p className="text-white font-medium">{submission.submission_deadline}</p>
              </div>
            </div>
            <div className="text-sm text-gray-400">
              Event Date: {submission.date}
            </div>
          </div>
        )}

        {/* Payout Status */}
        {submission.status === "approved" && (
          <div className="bg-gray-900 border border-gray-800 p-4 mb-6 flex items-center gap-4" data-testid="artist-payout-status">
            <DollarSign size={20} className={
              submission.payout_status === "paid" ? "text-green-400" :
              submission.payout_status === "processing" ? "text-amber-400" :
              "text-gray-500"
            } />
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider">Payout Status</p>
              <p className={`font-semibold uppercase tracking-wider text-sm ${
                submission.payout_status === "paid" ? "text-green-400" :
                submission.payout_status === "processing" ? "text-amber-400" :
                "text-gray-400"
              }`}>
                {submission.payout_status === "paid" ? "Paid" :
                 submission.payout_status === "processing" ? "Processing" :
                 "Pending"}
              </p>
            </div>
          </div>
        )}

        {/* Artist Details */}
        <div className="bg-gray-900 border border-gray-800 p-6 mb-6">
          <h2 className="font-heading text-xl text-white mb-6">ARTIST DETAILS</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-gray-800">
                <User size={20} className="text-gray-400" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Name</p>
                <p className="font-medium text-white">{submission.artist_name}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="p-2 bg-gray-800">
                <MapPin size={20} className="text-gray-400" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">City</p>
                <p className="font-medium text-white">{submission.city}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="p-2 bg-gray-800">
                <MapPin size={20} className="text-gray-400" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Venue</p>
                <p className="font-medium text-white">{submission.venue}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="p-2 bg-gray-800">
                <Calendar size={20} className="text-gray-400" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Date</p>
                <p className="font-medium text-white">{submission.date}</p>
              </div>
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-gray-800 flex items-start gap-3">
            <div className="p-2 bg-gray-800">
              <Hash size={20} className="text-gray-400" />
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider">S-User ID</p>
              <p className="font-mono text-white">{submission.s_user_id}</p>
            </div>
          </div>
        </div>

        {/* Media Uploads */}
        <div className="bg-gray-900 border border-gray-800 p-6 mb-6">
          <h2 className="font-heading text-xl text-white mb-6">MEDIA UPLOADS</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <MediaPreview url={submission.merchandise_url} type="image" label="Merchandise" />
            <MediaPreview url={submission.proof_of_play_url} type="video" label="Proof of Play" />
            <div className="border border-gray-800 p-4 bg-gray-900">
              <p className="text-gray-500 text-sm uppercase tracking-wider mb-2">Track Play Count</p>
              <div className="bg-black aspect-video flex items-center justify-center">
                <div className="text-center">
                  <PlayCircle size={40} className="mx-auto text-[#00B4E5] mb-2" />
                  <p className="text-4xl font-heading text-white">{submission.track_play_count}</p>
                  <p className="text-sm text-gray-500">times played</p>
                </div>
              </div>
            </div>
            <MediaPreview url={submission.bottle_console_url} type="video" label="Bottle next to Console" />
            <MediaPreview url={submission.branding_stories_post_url} type="image" label="Branding - Stories/Post" />
          </div>
        </div>

        {/* Timestamps */}
        <div className="text-sm text-gray-500 flex gap-6">
          <span>Created: {new Date(submission.created_at).toLocaleString()}</span>
          <span>Updated: {new Date(submission.updated_at).toLocaleString()}</span>
        </div>
      </div>
    </ArtistLayout>
  );
};

export default ArtistSubmissionDetail;
